import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from "react-tooltip";
import { toast } from 'sonner';
import DeleteConfirmationModal from '../Modal/DeleteConfirmationModal';
import DetailFactureModal from '../Modal/DetailFactureModal';
import BatchActions from './BatchActions';
import FacturationRow from './FacturationRow';
import '../style/facture.css';
import useSelectedDomain from '../../service/Hooks/useSelectedDomain';
import { deleteFacturation, generateFacturationPdf, generatePendingPdfs } from '../../service/api/facturationService';

/**
 * COMPOSANT PRINCIPAL TABLEAU DES FACTURATIONS
 */
function FactureTable({ facturations, loading, onRefresh }) {
  const { t } = useTranslation('facturation');
  const selectedDomain = useSelectedDomain();
  const [deleteModal, setDeleteModal] = useState({ show: false, facturation: null });
  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [generatingAllPdfs, setGeneratingAllPdfs] = useState(false);

  const handleGeneratePdf = async (facturationId) => {
    if (!selectedDomain?.id) {
      toast.error(t('validation.errors.noDomain'));
      return;
    }

    setGeneratingPdf(facturationId);
    try {
      await generateFacturationPdf(facturationId, selectedDomain.id);
      toast.success(t('pdfActions.generated'));
      onRefresh();
    } catch {
      toast.error(t('errors.generatePdf'));
    } finally {
      setGeneratingPdf(null);
    }
  };

  const handleGenerateAllPdfs = async () => {
    if (!selectedDomain?.id) {
      toast.error(t('validation.errors.noDomain'));
      return;
    }

    setGeneratingAllPdfs(true);
    try {
      const result = await generatePendingPdfs(selectedDomain.id);
      if (result.success) {
        toast.success(result.message);
        onRefresh();
      }
    } catch (error) {
      toast.error(t('errors.generatePdf'), error);
    } finally {
      setGeneratingAllPdfs(false);
    }
  };

  const handleDeleteClick = (facturation) => {
    setDeleteModal({ show: true, facturation });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.facturation) return;

    try {
      await deleteFacturation(deleteModal.facturation.id);
      toast.success(t('success.deleted'));
      setDeleteModal({ show: false, facturation: null });
      onRefresh();
    } catch (error) {
      toast.error(t('errors.delete'), error);
    }
  };

  const pendingPdfCount = facturations.filter(f => !f.image).length;

  const handleBatchOperationComplete = () => {
    onRefresh();
  };

  if (loading) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
          <p className="mt-3 text-muted">{t('table.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Actions par lot */}
      <BatchActions 
        facturations={facturations}
        onOperationComplete={handleBatchOperationComplete}
      />

      {/* Tableau principal */}
      <div className="card border-0 rounded-3 shadow-sm">
        <div className="card-header bg-white rounded-3 d-flex justify-content-between align-items-center">
          <div >
            <h5 className="card-title mb-0 fw-bold">{t('table.title')}</h5>
            <small className="text-muted">
              {t('table.subtitle')}
            </small>
          </div>
          
          {pendingPdfCount > 0 && (
            <button
              className="btn btn-warning btn-sm"
              onClick={handleGenerateAllPdfs}
              disabled={generatingAllPdfs}
            >
              {generatingAllPdfs ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {t('pdfActions.generating')}
                </>
              ) : (
                <>
                  <i className="fas fa-file-pdf me-2"></i>
                  {t('page.actions.generateAllPdfs')} ({pendingPdfCount})
                </>
              )}
            </button>
          )}
        </div>

        <div className="card-body py-3">
          {facturations.length === 0 ? (
            <div className="text-center py-5">
              <div className="text-muted">
                <i className="fas fa-file-invoice fa-3x mb-3 opacity-25"></i>
                <p className="mb-0">{t('table.noData.title')}</p>
                <small>{t('table.noData.description')}</small>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-striped table-sm mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="align-middle">{t('table.columns.client')}</th>
                    <th className="align-middle">{t('table.columns.totalAuc')}</th>
                    <th className="align-middle">{t('table.columns.totalCash')}</th>
                    <th className="align-middle">{t('table.columns.commission')}</th>
                    <th className="align-middle">{t('table.columns.commissionStatus')}</th>
                    <th className="align-middle text-center">{t('table.columns.payment')}</th>
                    <th className="align-middle text-center">{t('table.columns.status')}</th>
                    <th className="align-middle text-center">{t('table.columns.netToPay')}</th>
                    <th className="align-middle text-center">{t('table.columns.invoiceDate')}</th>
                    <th className="align-middle text-center">{t('table.columns.pdf')}</th>
                    <th className="align-middle text-center">{t('table.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {facturations.map((facturation) => (
                    <FacturationRow 
                      key={facturation.id}
                      facturation={facturation}
                      onRefresh={onRefresh}
                      selectedDomain={selectedDomain}
                      onDeleteClick={handleDeleteClick}
                      onGeneratePdf={handleGeneratePdf}
                      generatingPdf={generatingPdf}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Tooltips */}
      <Tooltip id="tooltip-validate" place="top" content={t('rowActions.validate.tooltip')} />
      <Tooltip id="tooltip-commission" place="top" content={t('rowActions.commission.tooltip')} />
      <Tooltip id="tooltip-final" place="top" content={t('rowActions.final.tooltip')} />
      <Tooltip id="tooltip-completed" place="top" content={t('rowActions.completed.tooltip')} />
      <Tooltip id="tooltip-view" place="top" content={t('rowActions.view.tooltip')} />
      <Tooltip id="tooltip-delete" place="top" content={t('rowActions.delete.tooltip')} />
      <Tooltip id="tooltip-generate" place="top" content={t('rowActions.generatePdf.tooltip')} />

      {/* Modals */}
      <DeleteConfirmationModal
        show={deleteModal.show}
        facturation={deleteModal.facturation}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteModal({ show: false, facturation: null })}
        t={t}
      />

      <DetailFactureModal />
    </>
  );
}

export default FactureTable;